import { createContext, useContext, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LANGUAGES = [
  { code: "en", name: "English", native: "English" },
  { code: "hi", name: "Hindi", native: "हिन्दी" },
  { code: "ml", name: "Malayalam", native: "മലയാളം" },
  { code: "ta", name: "Tamil", native: "தமிழ்" },
  { code: "te", name: "Telugu", native: "తెలుగు" },
  { code: "kn", name: "Kannada", native: "ಕನ್ನಡ" },
  { code: "bn", name: "Bengali", native: "বাংলা" },
  { code: "mr", name: "Marathi", native: "मराठी" },
  { code: "gu", name: "Gujarati", native: "ગુજરાતી" },
  { code: "pa", name: "Punjabi", native: "ਪੰਜਾਬੀ" },
  { code: "ur", name: "Urdu", native: "اردو", rtl: true },
  { code: "or", name: "Odia", native: "ଓଡ଼ିଆ" },
  { code: "as", name: "Assamese", native: "অসমীয়া" },
  { code: "ne", name: "Nepali", native: "नेपाली" },
  { code: "si", name: "Sinhala", native: "සිංහල" },
  { code: "es", name: "Spanish", native: "Español" },
  { code: "fr", name: "French", native: "Français" },
  { code: "de", name: "German", native: "Deutsch" },
  { code: "pt", name: "Portuguese", native: "Português" },
  { code: "it", name: "Italian", native: "Italiano" },
  { code: "nl", name: "Dutch", native: "Nederlands" },
  { code: "ru", name: "Russian", native: "Русский" },
  { code: "uk", name: "Ukrainian", native: "Українська" },
  { code: "pl", name: "Polish", native: "Polski" },
  { code: "tr", name: "Turkish", native: "Türkçe" },
  { code: "el", name: "Greek", native: "Ελληνικά" },
  { code: "cs", name: "Czech", native: "Čeština" },
  { code: "sv", name: "Swedish", native: "Svenska" },
  { code: "no", name: "Norwegian", native: "Norsk" },
  { code: "da", name: "Danish", native: "Dansk" },
  { code: "fi", name: "Finnish", native: "Suomi" },
  { code: "hu", name: "Hungarian", native: "Magyar" },
  { code: "ro", name: "Romanian", native: "Română" },
  { code: "ar", name: "Arabic", native: "العربية", rtl: true },
  { code: "he", name: "Hebrew", native: "עברית", rtl: true },
  { code: "fa", name: "Persian", native: "فارسی", rtl: true },
  { code: "sw", name: "Swahili", native: "Kiswahili" },
  { code: "am", name: "Amharic", native: "አማርኛ" },
  { code: "yo", name: "Yoruba", native: "Yorùbá" },
  { code: "ha", name: "Hausa", native: "Hausa" },
  { code: "zh", name: "Chinese (Simplified)", native: "简体中文" },
  { code: "zh-TW", name: "Chinese (Traditional)", native: "繁體中文" },
  { code: "ja", name: "Japanese", native: "日本語" },
  { code: "ko", name: "Korean", native: "한국어" },
  { code: "vi", name: "Vietnamese", native: "Tiếng Việt" },
  { code: "th", name: "Thai", native: "ไทย" },
  { code: "id", name: "Indonesian", native: "Bahasa Indonesia" },
  { code: "ms", name: "Malay", native: "Bahasa Melayu" },
  { code: "fil", name: "Filipino", native: "Filipino" },
  { code: "my", name: "Burmese", native: "မြန်မာ" },
  { code: "km", name: "Khmer", native: "ខ្មែរ" },
];

const I18nCtx = createContext(null);

export function I18nProvider({ children }) {
  const registry = useRef(new Set());
  const [lang, setLangState] = useState("en");
  const [map, setMap] = useState({});
  const [loading, setLoading] = useState(false);

  const tr = useCallback(
    (s) => {
      if (!s) return s;
      registry.current.add(s);
      return map[s] || s;
    },
    [map]
  );

  const setLang = async (code) => {
    const meta = LANGUAGES.find((l) => l.code === code);
    setLangState(code);
    document.documentElement.dir = meta?.rtl ? "rtl" : "ltr";
    if (code === "en") {
      setMap({});
      return;
    }
    const texts = Array.from(registry.current);
    try {
      const cached = JSON.parse(localStorage.getItem(`wp_tr_${code}`) || "{}");
      if (texts.every((t) => cached[t])) {
        setMap(cached);
        return;
      }
    } catch (_) { /* ignore */ }
    setLoading(true);
    toast.info(`Translating into ${meta?.name}…`, { duration: 8000 });
    try {
      const res = await axios.post(`${API}/translate`, { target_lang: meta?.name || code, texts }, { timeout: 120000 });
      const m = {};
      texts.forEach((t, i) => (m[t] = res.data.translations[i]));
      setMap(m);
      try { localStorage.setItem(`wp_tr_${code}`, JSON.stringify(m)); } catch (_) { /* ignore */ }
      toast.success(`Page translated into ${meta?.native || code}`);
    } catch (e) {
      toast.error("Translation failed. Please try again.");
      setLangState("en");
      document.documentElement.dir = "ltr";
    } finally {
      setLoading(false);
    }
  };

  return <I18nCtx.Provider value={{ lang, setLang, tr, loading }}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);
